/**
 * Voters — the BASELINE import: one `POST Voters` per row.
 *
 * Used only while api.command predates `import-batches`. Extracted from `hooks/import-voters.tsx`
 * so its outcomes are testable: every row ends as exactly one of
 *
 *   added    — the server created it
 *   already  — the server says the phone or VIN is already registered (nothing to do)
 *   fix      — anything else, with a reason code the "rows to fix" file explains
 *
 * Pause, abort, 401-pauses-not-fails and 429-waits behave as in `batch-uploader.ts`.
 */

import { isAlreadyRegisteredCode, readVoterError } from './voter-errors';

export type RowOutcome = { kind: 'added' } | { kind: 'already' | 'fix'; code: string; message: string };

export interface ILegacyJob<P> {
	key: string;
	payload: P;
}

export interface ILegacyUploadOptions<P> {
	jobs: readonly ILegacyJob<P>[];
	send: (payload: P) => Promise<unknown>;
	control: { aborted: boolean; paused: boolean };
	onStart?: (key: string) => void;
	onRow: (key: string, outcome: RowOutcome, index: number) => void;
	onSignedOut?: () => void;
	sleep?: (ms: number) => Promise<void>;
	maxRetries?: number;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** One failed request → the row's outcome. Exported for the tests. */
export const classifyRowError = (error: unknown): RowOutcome => {
	const parsed = readVoterError(error);
	switch (parsed.kind) {
		case 'duplicate-phone':
			return { kind: 'already', code: 'DUPLICATE_PHONE', message: parsed.message };
		case 'duplicate-vin':
			return { kind: 'already', code: 'DUPLICATE_VIN', message: parsed.message };
		case 'duplicate-email':
			return { kind: 'fix', code: 'DUPLICATE_EMAIL', message: parsed.message };
		case 'invalid-pu':
			return { kind: 'fix', code: 'INVALID_POLLING_UNIT', message: parsed.message };
		case 'core-unavailable':
			return { kind: 'fix', code: 'CORE_UNAVAILABLE', message: parsed.message };
		default:
			return {
				kind: 'fix',
				code: parsed.field ? `INVALID_${parsed.field.toUpperCase()}` : 'SERVER_REJECTED',
				message: parsed.message,
			};
	}
};

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

export interface ILegacyUploadSummary {
	outcome: 'completed' | 'aborted';
	added: number;
	already: number;
	fix: number;
	processed: number;
}

export const runLegacyUpload = async <P>(options: ILegacyUploadOptions<P>): Promise<ILegacyUploadSummary> => {
	const { jobs, send, control, onRow, onStart } = options;
	const sleep = options.sleep ?? defaultSleep;
	const maxRetries = options.maxRetries ?? 2;
	const summary: ILegacyUploadSummary = { outcome: 'completed', added: 0, already: 0, fix: 0, processed: 0 };

	for (let i = 0; i < jobs.length; i++) {
		while (control.paused && !control.aborted) await sleep(200);
		if (control.aborted) return { ...summary, outcome: 'aborted' };

		const job = jobs[i];
		onStart?.(job.key);
		let attempt = 0;
		let outcome: RowOutcome | null = null;

		while (!outcome) {
			try {
				await send(job.payload);
				outcome = { kind: 'added' };
			} catch (error) {
				const status = statusOf(error);
				if (status === 401) {
					control.paused = true;
					options.onSignedOut?.();
					while (control.paused && !control.aborted) await sleep(200);
					if (control.aborted) return { ...summary, outcome: 'aborted' };
					continue;
				}
				if (status === 429) {
					await sleep(5000);
					continue;
				}
				if ((status === undefined || status >= 500) && status !== 503 && attempt < maxRetries) {
					attempt += 1;
					await sleep(1000 * 2 ** (attempt - 1));
					continue;
				}
				outcome = classifyRowError(error);
			}
		}

		summary.processed += 1;
		if (outcome.kind === 'added') summary.added += 1;
		else if (outcome.kind === 'already' || isAlreadyRegisteredCode(outcome.code)) summary.already += 1;
		else summary.fix += 1;
		onRow(job.key, outcome, i);
	}

	return summary;
};
